import React from 'react';
import { Panel } from '@tanium/react-panel';
import { Box } from '@tanium/react-box';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import MediumBoldText from 'components/styles/text/MediumBoldText';
import MediumText from 'components/styles/text/MediumText';
import IAccessConfiguration from 'common/IAccessConfiguration';
import { useDarkTheme } from 'common/hooks/useDarkTheme/useDarkTheme';
import { RequestAccess } from 'gql/Request';
import { getAccessLevelColor } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessLevelConfigDisplay';
import Tooltip from 'components/tooltip/Tooltip';
import { IApprovalLevelStrings } from 'common/IApprovalLevel';
import StatusIcon, { IIconTypes } from 'components/icons/StatusIcon';

export interface IAccessConfigurationProps {
  accessConfigurations?: IAccessConfiguration[] | [] | undefined | null;
  targetAccessLookup?: Record<string, string>;
  individualPanel?: boolean;
}

export const AccessConfiguration = ({
  accessConfigurations,
  targetAccessLookup,
  individualPanel = false,
}: IAccessConfigurationProps): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDarkMode] = useDarkTheme();

  const component = (
    <DefinitionListItem
      key={'access-configurations'}
      term={<MediumBoldText>{t('ACCESS_CONFIGURATION')}:</MediumBoldText>}
      description={accessConfigurations?.map((accessConfig, i) => {
        return (
          <Box
            key={`$access_configuration_${i}`}
            display={'inline-block'}
            border={1}
            borderColor={isDarkMode ? theme.colors.HeaderTier2 : theme.colors.FillLevel3}
            borderRadius={'0.2em'}
            mb={1}
            mr={2}
          >
            {/* CARD HEADER */}
            <Box background={isDarkMode ? theme.colors.HeaderTier2 : theme.colors.FillLevel3}>
              <Stack itemSpacing={2} flexWrap={'wrap'}>
                <StackChild grow={1}>
                  <DefinitionList>
                    <DefinitionListItem
                      term={
                        <MediumText>
                          <Box py={2} pl={2} pr={1} color={theme.colors.subtle}>
                            {t('ACCESS_LEVEL')}:
                          </Box>
                        </MediumText>
                      }
                      description={
                        <Box pr={2}>
                          <Tooltip
                            content={`${t('CAN_BE_APPROVED_BY')} ${t(IApprovalLevelStrings[accessConfig.level])}`}
                            placement="top"
                          >
                            <Box
                              display={'inline-block'}
                              color={getAccessLevelColor(accessConfig.level)}
                            >{`${accessConfig.level}`}</Box>
                            <Box display={'inline-block'} ml={'0.3em'}>
                              <StatusIcon type={IIconTypes.INFO} props={{ outerRadius: 6, innerRadius: 5 }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </DefinitionList>
                </StackChild>
              </Stack>
            </Box>

            {/* CARD BODY */}
            <Box background={isDarkMode ? theme.colors.HeaderTier3 : theme.colors.FillLevel1} py={1} px={2}>
              <Stack itemSpacing={1} flexWrap={'wrap'}>
                <StackChild grow={1}>
                  {accessConfig.access.map((access: RequestAccess, j, accesses) => {
                    // Set label to name if exists in lookup else ID
                    let targetAccessLabel = access.targetAccessID;
                    if (targetAccessLookup && access.targetAccessID) {
                      targetAccessLabel = targetAccessLookup[access.targetAccessID]
                        ? targetAccessLookup[access.targetAccessID]
                        : targetAccessLabel;
                    }
                    return (
                      <Box
                        key={`${j}-access`}
                        py={1}
                        borderBottom={
                          j < accesses.length - 1
                            ? `1px solid ${isDarkMode ? theme.colors.HeaderTier2 : theme.colors.FillLevel3}`
                            : ''
                        }
                      >
                        <DefinitionList>
                          <DefinitionListItem term={t('ACCESS_TYPE')} description={access.type} />
                          {[
                            [access.apiAccessType, t('API_ACCESS_TYPE')],
                            [access.namespace, t('NAMESPACE')],
                            [access.role, t('ROLE')],
                            [access.bastionType, t('BASTION_TYPE')],
                          ].map(([field, label]) => {
                            return field ? <DefinitionListItem term={label} description={field} /> : '';
                          })}
                        </DefinitionList>
                        {access.targetAccessID && (
                          <Box pl={3}>
                            <MediumText>
                              <DefinitionListItem
                                key={`${j}-targetAccessID`}
                                term={t('TARGET')}
                                description={targetAccessLabel}
                              />
                            </MediumText>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </StackChild>
              </Stack>
            </Box>
          </Box>
        );
      })}
    />
  );

  return individualPanel ? (
    <Panel title={t('TBAC_INFO')}>
      <DefinitionList>{component}</DefinitionList>
    </Panel>
  ) : (
    component
  );
};

export default AccessConfiguration;
